// Broker product registry — the single source of truth for the per-product differences
// in the broker portal (Business Plus vs Individual Plus, and future managed products).
//
// Goal (PRD §4A.1 / guardrail G5 — scalable, data-driven, no scattered `if (business)`):
// adding the next managed product should be ONE entry here, not edits across
// BrokerApplicationNew / BrokerApplicationDetail / allRoutes / BrokerService.
//
// Consumers:
//   - T-F1 BrokerService.js .............. implements the methods named in `service`.
//   - T-F2 BrokerApplicationNew.jsx ...... renders `preCreateFields`, builds the create
//                                          payload via `buildCreatePayload`, calls
//                                          BrokerService[service.create].
//   - T-F3 BrokerApplicationDetail.jsx ... uses `service.detail` / `fieldsGroup` / `labelFields`.
//   - T-F4 allRoutes.js edit dispatcher .. picks the apply component by `editComponent`,
//                                          resolved from the draft's `formType`.
//
// This module is intentionally pure config: it imports nothing and performs no I/O, so it
// can be consumed from anywhere without cycles. Service calls are referenced by METHOD NAME
// (looked up as BrokerService[name]) and `buildCreatePayload` receives the caller's existing
// `{ trimValue, normalizeEmail }` helpers, so trimming/normalization stays consistent with
// the rest of the form layer instead of being duplicated here.

// Fields collected for EVERY product in the "New application" chooser, before a draft exists.
// (Client email + the broker's own reference id — Q7.) Product-specific fields are in
// each product's `preCreateFields`.
export const BROKER_SHARED_PRE_CREATE_FIELDS = [
  { name: "email", label: "Client Email", type: "email", required: true },
  { name: "brokerReferenceId", label: "Client Reference ID", type: "text", required: true },
];

export const BROKER_PRODUCTS = {
  business_plus: {
    key: "business_plus",
    label: "Business Plus",
    enabled: true,
    // Drives the broker edit dispatcher (T-F4) and detail branch (T-F3).
    formType: "BUSINESS",
    type: 1,
    // fields-json group for label/value resolution on the detail screen.
    fieldsGroup: "Business",
    editComponent: "ApplyBusinessV2",
    // BrokerService method names (T-F1 guarantees these exist).
    service: {
      create: "createApplication",
      update: "updateApplication",
      submit: "submitApplication",
      detail: "businessDetail",
      fields: "businessFields",
    },
    // Product-specific fields shown in the chooser in addition to the shared ones.
    preCreateFields: [
      { name: "Business_Name", label: "Client Name", type: "text", required: true },
    ],
    // Mirrors the existing BrokerApplicationNew payload exactly (BrokerApplicationNew.jsx:93-100).
    // accountType is pinned to the legacy literal "business" (NOT the registry key) so the business
    // create payload is byte-identical to before this refactor — the backend/stored value is unchanged.
    buildCreatePayload: (form, { trimValue, normalizeEmail }) => ({
      ...form,
      brokerReferenceId: trimValue(form.brokerReferenceId),
      Business_Name: trimValue(form.Business_Name),
      email: normalizeEmail(form.email),
      accountType: "business",
      type: 1,
    }),
  },

  individual_plus: {
    key: "individual_plus",
    label: "Individual Plus",
    enabled: true,
    formType: "INDIVIDUAL_PLUS",
    // Individuals have no `type` switch.
    type: null,
    // SAME group the live ApplyIndividualLite flow uses (Q8) → sysConfig.individualPlusJSON.
    fieldsGroup: "Individuals_Lite",
    editComponent: "ApplyIndividualLite",
    service: {
      create: "createIndividualApplication",
      update: "updateIndividualApplication",
      submit: "submitIndividualApplication",
      detail: "individualDetail",
      fields: "individualFields",
    },
    // Q7: First name, Last name (+ optional middle); email + reference id are shared.
    preCreateFields: [
      { name: "firstName", label: "First Name", type: "text", required: true },
      { name: "middleName", label: "Middle Name", type: "text", required: false },
      { name: "lastName", label: "Last Name", type: "text", required: true },
    ],
    // Maps the chooser form to the IndividualLite create payload (T0.2). brokerId/createdBy are
    // stamped server-side in prepareBrokerApplication (T-B2) — never sent from the client.
    buildCreatePayload: (form, { trimValue, normalizeEmail }) => ({
      firstName: trimValue(form.firstName),
      middleName: trimValue(form.middleName) || null,
      lastName: trimValue(form.lastName),
      email: normalizeEmail(form.email),
      brokerReferenceId: trimValue(form.brokerReferenceId),
    }),
  },
};

export const getBrokerProduct = (key) => BROKER_PRODUCTS[key] || null;

export const getEnabledBrokerProducts = () =>
  Object.values(BROKER_PRODUCTS).filter((product) => product.enabled);

// Resolve a product from a draft's formType — used by the edit-route dispatcher (T-F4)
// and the detail branch (T-F3). INDIVIDUAL_PLUS → individual_plus, BUSINESS* → business_plus.
export const getBrokerProductByFormType = (formType) => {
  if (!formType) return null;
  if (formType === "INDIVIDUAL_PLUS") return BROKER_PRODUCTS.individual_plus;
  if (["BUSINESS", "BUSINESS_PLUS", "BUSINESS_PRIME"].includes(formType)) return BROKER_PRODUCTS.business_plus;
  return null;
};
